import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
    selectFormState,
    selectInvoicePositions,
    selectTicketCategoryDetails,
    selectV2Totals
} from "../../../../store/slices/invoiceV2Slice";
import { formatCurrencyToLocale, roundToTwo } from "../../../../utils/method";

function InvoiceSummary() {
    const { t } = useTranslation();

    const formValues = useSelector(selectFormState);
    const invoicePositions = useSelector(selectInvoicePositions);
    const ticketCategoryDetails = useSelector(selectTicketCategoryDetails);
    const v2Totals = useSelector(selectV2Totals);

    const calc = useMemo(() => {
        const salesTaxPercentage = formValues.salesTaxPercentage || ticketCategoryDetails?.vat || 0;

        if (v2Totals?.positionCount > 0) {
            const net = v2Totals.invoiceAmount || 0;
            const tax = roundToTwo((net * salesTaxPercentage) / 100);
            const gross = roundToTwo(net + tax);
            const prev = roundToTwo(formValues.previousInvoiceGrossAmount || 0);
            return { net, tax, gross, prev, final: roundToTwo(gross - prev), vat: salesTaxPercentage };
        }

        let net = 0;
        invoicePositions.forEach((pos) => {
            const qty = pos?.invoice?.quantity || 0;
            if (qty > 0) {
                const price = pos?.lv?.unitPrice || 0;
                if (price > 0) net += qty * parseFloat(price);
            }
        });
        const tax = roundToTwo((net * salesTaxPercentage) / 100);
        const gross = roundToTwo(net + tax);
        const prev = roundToTwo(formValues.previousInvoiceGrossAmount || 0);
        return { net: roundToTwo(net), tax, gross, prev, final: roundToTwo(gross - prev), vat: salesTaxPercentage };
    }, [invoicePositions, v2Totals, formValues.salesTaxPercentage, formValues.previousInvoiceGrossAmount, ticketCategoryDetails?.vat]);

    return (
        <div className="invoice-summary-footer">
            <div className="summary-footer-title">{t("invoice.totals")}</div>
            <div className="summary-footer-row">
                <span>{t("invoice.sum-all-positions")}</span>
                <span>{formatCurrencyToLocale(calc.net)}</span>
            </div>
            <div className="summary-footer-row">
                <span>{t("invoice.vat-rate")}</span>
                <span>{calc.vat}%</span>
            </div>
            <div className="summary-footer-row">
                <span>{t("invoice.vat")}</span>
                <span>{formatCurrencyToLocale(calc.tax)}</span>
            </div>
            <div className="summary-footer-row summary-footer-total">
                <span>{t("invoice.gross")}</span>
                <span>{formatCurrencyToLocale(calc.gross)}</span>
            </div>
            {calc.prev > 0 && (
                <>
                    <div className="summary-footer-row">
                        <span>{t("invoice.previous-invoice-amount")}</span>
                        <span>−{formatCurrencyToLocale(calc.prev)}</span>
                    </div>
                    <div className="summary-footer-row summary-footer-final">
                        <span>{t("invoice.invoice-amount")}</span>
                        <span>{formatCurrencyToLocale(calc.final)}</span>
                    </div>
                </>
            )}
        </div>
    );
}

export default InvoiceSummary;
